import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

const publicHeroImageUrl = (id) => {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
  return `${base}/public/hero-images/${id}`;
};

/** Auto-advancing hero carousel driven by the admin's uploaded slides. */
export default function HeroSlider({ tenantSlug = "abrah", intervalMs = 6000 }) {
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/hero-slides?tenant_slug=${tenantSlug}`);
        if (!cancelled) setSlides(data || []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [tenantSlug]);

  const go = useCallback((n) => {
    if (slides.length === 0) return;
    setIdx(((n % slides.length) + slides.length) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), intervalMs);
    return () => clearInterval(t);
  }, [slides.length, intervalMs]);

  if (slides.length === 0) return null;

  const active = slides[idx];

  return (
    <section className="relative w-full overflow-hidden bg-emerald-950" data-testid="hero-slider">
      <div className="relative aspect-[21/9] max-h-[560px] w-full">
        {slides.map((s, i) => (
          <img
            key={s.id}
            src={publicHeroImageUrl(s.image_file_id)}
            alt={s.title || "banner"}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${
              i === idx ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}
        {/* Gradient scrim for text legibility */}
        {(active.title || active.subtitle || active.cta_label) && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        )}
        {/* Text + CTA overlay */}
        <div className="absolute inset-0 flex items-end sm:items-center">
          <div className="max-w-6xl mx-auto w-full px-5 sm:px-8 pb-10 sm:pb-0">
            <div className="max-w-xl text-white" data-testid="hero-slide-copy">
              {active.title && (
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight drop-shadow">
                  {active.title}
                </h2>
              )}
              {active.subtitle && (
                <p className="mt-3 text-sm sm:text-base text-emerald-50/95 max-w-lg drop-shadow">
                  {active.subtitle}
                </p>
              )}
              {active.cta_label && active.cta_url && (
                <div className="mt-5">
                  <SlideCta label={active.cta_label} url={active.cta_url} />
                </div>
              )}
            </div>
          </div>
        </div>
        {slides.length > 1 && (
          <>
            <button aria-label="Previous slide" onClick={() => go(idx - 1)}
              data-testid="hero-prev"
              className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full
                         bg-white/20 hover:bg-white/40 backdrop-blur text-white items-center justify-center transition">
              <ChevronLeft size={18} />
            </button>
            <button aria-label="Next slide" onClick={() => go(idx + 1)}
              data-testid="hero-next"
              className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full
                         bg-white/20 hover:bg-white/40 backdrop-blur text-white items-center justify-center transition">
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
              {slides.map((_, i) => (
                <button key={i} onClick={() => go(i)} aria-label={`Slide ${i + 1}`}
                  data-testid={`hero-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-6 bg-amber-400" : "w-1.5 bg-white/50 hover:bg-white/80"
                  }`} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function SlideCta({ label, url }) {
  const isExternal = /^https?:\/\//i.test(url);
  const cls =
    "inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-emerald-950 " +
    "font-semibold text-sm px-5 py-2.5 rounded-full shadow-lg transition-colors";
  if (isExternal) {
    return <a href={url} target="_blank" rel="noreferrer" className={cls} data-testid="hero-cta">{label}</a>;
  }
  return <Link to={url} className={cls} data-testid="hero-cta">{label}</Link>;
}
